// CONTACT REQUESTS — the platform's lead inbox.
//
// A visitor on a tenant's public site, or a doctor looking at a custom plan,
// fills the "contact us" form and a row lands in `contact_inquiries`. Nobody
// answers it from inside a hospital: these are SALES leads about the platform
// itself, which is why the API (api/routes/contactInquiryRoutes.js) is
// super_admin only — a tenant admin reading the list would see every other
// hospital's enquiries.
//
// That gate is also why this page has no view/manage split and no
// `useCanManage` call, unlike the master screens it otherwise copies
// (table + mobile cards + locale namespace): the API answers `super_admin`
// or nobody, there is no catalogue key for it, and everyone who can open the
// page can mark a row responded. Same shape as the Email Queue screen —
// SuperAdminRoute in App.jsx, `superAdminOnly` in config/platformNav.js.
//
// The list endpoint returns EVERY row in one response (no server paging), so
// the tab, the search and the pager here are all client-side. That is the
// honest match for the data — a handful of leads a week — and it keeps the
// counts on the tabs truthful, which a server-paged list could not do without
// a second call.
import React, { useEffect, useMemo, useState } from 'react';
// Uppercase alias so the JSX-only usage passes this repo's no-unused-vars
// config (varsIgnorePattern ^[A-Z_]), which doesn't count JSX references.
import { motion as Motion } from 'framer-motion';
import { Mail, Phone, CheckCircle2, Clock, Search, Inbox, RefreshCw, Stethoscope, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import Pagination from '../components/Pagination';
import { getContactInquiries, setInquiryResponded } from '../redux/contactInquirySlice';
import { formatTime } from '../utils/clock';

const MAX_SEARCH_LENGTH = 50;
const TABS = ['all', 'pending', 'responded'];

// created_at / createdAt: the model declares plain `timestamps: true`, but the
// MongoDB-era tables in this codebase carry the snake_case pair, so read both
// rather than print a dash when the column is the other one.
const createdAtOf = (row) => row?.createdAt || row?.created_at || null;

const fmtDateTime = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    return isNaN(d.getTime())
        ? '—'
        // "15 Oct 2026, 2:30 PM" — the time of day is 12-hour (utils/clock.js).
        : `${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}, ${formatTime(d)}`;
};

const ContactInquiries = () => {
    const { t } = useTranslation('contactInquiries');
    const dispatch = useDispatch();
    const { inquiries, loading, error } = useSelector((s) => s.contactInquiry);

    const [activeTab, setActiveTab] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    // Which row is mid-PUT — so two fast clicks cannot send two toggles, and
    // the one button that is working says so instead of the whole table.
    const [savingId, setSavingId] = useState(null);

    useEffect(() => {
        dispatch(getContactInquiries());
    }, [dispatch]);

    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, searchQuery]);

    const counts = useMemo(() => ({
        all: inquiries.length,
        pending: inquiries.filter((i) => !i.responded).length,
        responded: inquiries.filter((i) => i.responded).length,
    }), [inquiries]);

    // Search across everything a Super Admin would recognise a lead by — the
    // person, how to reach them, the plan they asked about and what they
    // wrote. The API has no search parameter, so this is the only one.
    const filtered = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        return inquiries
            .filter((i) => (activeTab === 'all' ? true : activeTab === 'responded' ? i.responded : !i.responded))
            .filter((i) => {
                if (!q) return true;
                return [i.name, i.email, i.phone, i.packageName, i.notes, i.doctorId]
                    .some((v) => String(v || '').toLowerCase().includes(q));
            });
    }, [inquiries, activeTab, searchQuery]);

    const pageRows = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filtered.slice(start, start + itemsPerPage);
    }, [filtered, currentPage, itemsPerPage]);

    const handlePageChange = (page, newLimit) => {
        if (newLimit && newLimit !== itemsPerPage) {
            setItemsPerPage(newLimit);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const toggleResponded = async (inquiry) => {
        if (savingId) return;
        const next = !inquiry.responded;
        setSavingId(inquiry.id);
        try {
            await dispatch(setInquiryResponded({ id: inquiry.id, responded: next })).unwrap();
            toast.success(next
                ? t('contactInquiries.marked_responded', 'Marked as responded.')
                : t('contactInquiries.marked_pending', 'Moved back to waiting.'));
        } catch (err) {
            toast.error(err || t('contactInquiries.mark_failed', 'Could not update this request.'));
        } finally {
            setSavingId(null);
        }
    };

    const SourceBadge = ({ inquiry }) => {
        const isDoctor = inquiry.source === 'doctor';
        const Icon = isDoctor ? Stethoscope : Globe;
        return (
            <span
                className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${isDoctor ? 'bg-[#4ABDBD]/10 text-[#2D8A8A] border-[#4ABDBD]/30' : 'bg-gray-100 text-gray-500 border-gray-200'}`}
                data-testid={`contact-inquiries-source-${inquiry.id}`}
            >
                <Icon size={11} />
                {isDoctor
                    ? t('contactInquiries.source_doctor', 'Doctor')
                    : t('contactInquiries.source_landing', 'Website')}
            </span>
        );
    };

    const ReachCell = ({ inquiry, testidPrefix }) => (
        <div className="flex flex-col gap-1">
            {inquiry.email ? (
                <a
                    href={`mailto:${inquiry.email}`}
                    className="inline-flex items-center gap-1.5 text-xs text-[#2D8A8A] hover:underline break-all"
                    data-testid={`${testidPrefix}-email-${inquiry.id}`}
                >
                    <Mail size={12} className="shrink-0" /> {inquiry.email}
                </a>
            ) : (
                <span className="text-xs text-gray-400">{t('contactInquiries.no_email', 'No e-mail')}</span>
            )}
            {/* `phone` is optional on the row — the column was added after the
                first leads were taken, so old rows simply have none. */}
            {inquiry.phone ? (
                <a
                    href={`tel:${inquiry.phone}`}
                    className="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:text-[#2D8A8A]"
                    data-testid={`${testidPrefix}-phone-${inquiry.id}`}
                >
                    <Phone size={12} className="shrink-0" /> {inquiry.phone}
                </a>
            ) : (
                <span className="text-[11px] text-gray-400" data-testid={`${testidPrefix}-no-phone-${inquiry.id}`}>
                    {t('contactInquiries.no_phone', 'No phone')}
                </span>
            )}
        </div>
    );

    const RespondedButton = ({ inquiry, testid }) => (
        <button
            type="button"
            onClick={() => toggleResponded(inquiry)}
            disabled={savingId === inquiry.id}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-300 shadow-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${inquiry.responded ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-gray-100 text-gray-600 hover:bg-[#4ABDBD] hover:text-white'}`}
            data-testid={testid}
        >
            <CheckCircle2 className="h-4 w-4" />
            {inquiry.responded
                ? t('contactInquiries.responded', 'Responded')
                : t('contactInquiries.mark_responded', 'Mark responded')}
        </button>
    );

    return (
        <div className="w-full" data-testid="contact-inquiries-container">
            <div className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4" data-testid="contact-inquiries-header">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] flex items-center justify-center shadow-md shrink-0">
                            <Mail size={22} className="text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent" data-testid="contact-inquiries-title">
                                {t('contactInquiries.title', 'Contact requests')}
                            </h1>
                            <p className="text-sm text-gray-500 max-w-3xl" data-testid="contact-inquiries-subtitle">
                                {t('contactInquiries.subtitle', 'Enquiries about custom plans, from the public sites and from doctors. Mark each one once the team has replied.')}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => dispatch(getContactInquiries())}
                        disabled={loading}
                        className="inline-flex items-center gap-2 px-4 py-2 border-2 border-gray-200 text-gray-600 rounded-xl hover:border-[#4ABDBD] hover:text-[#2D8A8A] transition-colors duration-300 shrink-0 cursor-pointer disabled:opacity-50"
                        data-testid="contact-inquiries-refresh-button"
                    >
                        <RefreshCw size={16} className={loading ? 'animate-spin' : undefined} />
                        {t('contactInquiries.refresh', 'Refresh')}
                    </button>
                </div>

                {/* Tabs + search */}
                <div className="mb-5 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid="contact-inquiries-tab-search-section">
                    <div className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2" data-testid="contact-inquiries-tabs">
                        {TABS.map((tab) => (
                            <button
                                key={tab}
                                type="button"
                                onClick={() => setActiveTab(tab)}
                                className={`relative px-5 py-2 text-sm rounded-xl transition-colors duration-300 z-10 ${activeTab === tab ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
                                data-testid={`contact-inquiries-tab-${tab}`}
                            >
                                {activeTab === tab && (
                                    <Motion.div
                                        layoutId="activeContactInquiryTab"
                                        className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                    />
                                )}
                                <span className="relative z-20">
                                    {t(`contactInquiries.tab_${tab}`, tab)}
                                    <span className="ml-1 text-xs opacity-70" data-testid={`contact-inquiries-tab-count-${tab}`}>({counts[tab]})</span>
                                </span>
                            </button>
                        ))}
                    </div>
                    <div className="relative group w-full sm:w-1/3" data-testid="contact-inquiries-search-container">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-[#4ABDBD] transition-colors" size={20} />
                        <input
                            type="text"
                            placeholder={t('contactInquiries.search_placeholder', 'Search by name, e-mail, phone or plan...')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            maxLength={MAX_SEARCH_LENGTH}
                            className="w-full pl-12 pr-4 py-2 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4ABDBD] focus:border-[#4ABDBD] focus:bg-white transition-all duration-300 text-gray-700 placeholder-gray-400"
                            data-testid="contact-inquiries-search-input"
                        />
                    </div>
                </div>

                {/* A failed load must say so — an empty state here would read as
                    "no leads", which is the one wrong answer. */}
                {error && !loading && inquiries.length === 0 && (
                    <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-800" data-testid="contact-inquiries-error">
                        {t('contactInquiries.load_failed', 'Could not load the contact requests.')}
                    </div>
                )}

                {loading && inquiries.length === 0 ? (
                    <div className="flex items-center justify-center py-16 sm:py-20" data-testid="contact-inquiries-loading-container">
                        <div className="flex flex-col items-center gap-4">
                            <div className="border-4 border-[#4ABDBD] border-t-transparent rounded-full w-12 h-12 animate-spin" data-testid="contact-inquiries-loading-spinner"></div>
                            <p className="text-gray-500 text-sm">{t('contactInquiries.loading', 'Loading contact requests...')}</p>
                        </div>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-16 sm:py-20" data-testid="contact-inquiries-empty-container">
                        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full mb-6">
                            <Inbox size={48} className="text-gray-400" />
                        </div>
                        <h3 className="text-xl sm:text-2xl font-semibold text-gray-700 mb-2" data-testid="contact-inquiries-empty-title">
                            {searchQuery.trim()
                                ? t('contactInquiries.none_found', 'No contact requests found')
                                : t('contactInquiries.none_yet', 'No contact requests yet')}
                        </h3>
                        <p className="text-gray-500 max-w-md mx-auto">
                            {searchQuery.trim()
                                ? t('contactInquiries.try_adjusting_search', 'Try adjusting your search terms to find what you’re looking for.')
                                : t('contactInquiries.none_yet_hint', 'Requests sent from the public sites and from doctors will appear here.')}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Desktop table */}
                        <div className="hidden sm:block overflow-x-auto rounded-md border border-gray-200 mb-4" data-testid="contact-inquiries-table-container">
                            <table className="w-full border-collapse rounded-md overflow-hidden" data-testid="contact-inquiries-table">
                                <thead>
                                    <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                        <th className="p-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="contact-inquiries-header-name">{t('contactInquiries.col_name', 'Who')}</th>
                                        <th className="p-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="contact-inquiries-header-reach">{t('contactInquiries.col_reach', 'Contact')}</th>
                                        <th className="p-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="contact-inquiries-header-plan">{t('contactInquiries.col_plan', 'Plan / source')}</th>
                                        <th className="p-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="contact-inquiries-header-notes">{t('contactInquiries.col_notes', 'Notes')}</th>
                                        <th className="p-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="contact-inquiries-header-created">{t('contactInquiries.col_created', 'Received')}</th>
                                        <th className="p-3 text-center text-xs font-bold uppercase tracking-wider" data-testid="contact-inquiries-header-responded">{t('contactInquiries.col_responded', 'Status')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageRows.map((inquiry) => (
                                        <tr
                                            key={inquiry.id}
                                            className={`border-b border-gray-100/50 transition-all duration-300 ${inquiry.responded ? 'bg-emerald-50/40' : 'hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80'}`}
                                            data-testid={`contact-inquiries-row-${inquiry.id}`}
                                        >
                                            <td className="p-3 text-xs font-semibold text-gray-800" data-testid={`contact-inquiries-row-name-${inquiry.id}`}>
                                                {inquiry.name}
                                            </td>
                                            <td className="p-3" data-testid={`contact-inquiries-row-reach-${inquiry.id}`}>
                                                <ReachCell inquiry={inquiry} testidPrefix="contact-inquiries-row" />
                                            </td>
                                            <td className="p-3" data-testid={`contact-inquiries-row-plan-${inquiry.id}`}>
                                                <div className="flex flex-col items-start gap-1">
                                                    <span className="text-xs text-gray-700">
                                                        {inquiry.packageName || t('contactInquiries.no_plan', 'No plan named')}
                                                    </span>
                                                    <SourceBadge inquiry={inquiry} />
                                                </div>
                                            </td>
                                            <td className="p-3 text-xs text-gray-600 max-w-xs" data-testid={`contact-inquiries-row-notes-${inquiry.id}`}>
                                                {inquiry.notes
                                                    ? <span className="whitespace-pre-wrap break-words">{inquiry.notes}</span>
                                                    : <span className="text-gray-400">—</span>}
                                            </td>
                                            <td className="p-3 text-xs text-gray-500 whitespace-nowrap" data-testid={`contact-inquiries-row-created-${inquiry.id}`}>
                                                <span className="inline-flex items-center gap-1"><Clock size={12} /> {fmtDateTime(createdAtOf(inquiry))}</span>
                                            </td>
                                            <td className="p-3 text-center">
                                                <RespondedButton inquiry={inquiry} testid={`contact-inquiries-toggle-${inquiry.id}`} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile cards — the same row, stacked. */}
                        <div className="block sm:hidden space-y-4 mb-4" data-testid="contact-inquiries-cards-container">
                            {pageRows.map((inquiry) => (
                                <div
                                    key={inquiry.id}
                                    className={`p-4 rounded-xl shadow-md transition-all duration-300 ${inquiry.responded ? 'bg-emerald-50/50' : 'bg-gray-50 hover:shadow-lg'}`}
                                    data-testid={`contact-inquiries-card-${inquiry.id}`}
                                >
                                    <div className="flex justify-between items-start gap-2 mb-2">
                                        <span className="text-sm font-bold text-gray-800 truncate">{inquiry.name}</span>
                                        <SourceBadge inquiry={inquiry} />
                                    </div>
                                    <div className="mb-3">
                                        <ReachCell inquiry={inquiry} testidPrefix="contact-inquiries-card" />
                                    </div>
                                    <div className="text-xs text-gray-700 mb-2">
                                        <span className="font-bold">{t('contactInquiries.col_plan', 'Plan / source')}:</span>{' '}
                                        {inquiry.packageName || t('contactInquiries.no_plan', 'No plan named')}
                                    </div>
                                    {inquiry.notes && (
                                        <p className="text-xs text-gray-600 whitespace-pre-wrap break-words mb-2">{inquiry.notes}</p>
                                    )}
                                    <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-200">
                                        <span className="text-[11px] text-gray-400 inline-flex items-center gap-1">
                                            <Clock size={12} /> {fmtDateTime(createdAtOf(inquiry))}
                                        </span>
                                        <RespondedButton inquiry={inquiry} testid={`contact-inquiries-card-toggle-${inquiry.id}`} />
                                    </div>
                                </div>
                            ))}
                        </div>

                        {filtered.length > 0 && (
                            <Pagination
                                totalItems={filtered.length}
                                currentPage={currentPage}
                                itemsPerPage={itemsPerPage}
                                onPageChange={handlePageChange}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ContactInquiries;
